import React, { useEffect, useMemo, useRef } from 'react';
import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';

import { Bar, BarChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/styles';

import ReactECharts from 'echarts-for-react';
import { format } from 'echarts/core';
import { useCommonTranslation } from 'c-translation';
import { useAuthenticatedUser } from 'c-hooks';
import { pieTooltipHtml } from './pieTooltip';

const HIDE_ZERO_OVERDELIVERY_EMAILS = ['sam.holland+openaius@uniledsoftware.com'];

type WrappedAxisTickProps = {
    x?: number;
    y?: number;
    payload?: { value: string };
    splitCamelCase?: boolean;
    fontSize?: number;
};

const hexToHsl = (hex: string): { h: number; s: number; l: number } | null => {
    const match = hex.replace('#', '').match(/^([0-9a-f]{6})$/i);
    if (!match) return null;

    const int = parseInt(match[1], 16);
    // eslint-disable-next-line no-bitwise
    const r = ((int >> 16) & 255) / 255;
    // eslint-disable-next-line no-bitwise
    const g = ((int >> 8) & 255) / 255;
    // eslint-disable-next-line no-bitwise
    const b = (int & 255) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;

    let h = 0;
    const l = (max + min) / 2;
    const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));

    if (delta !== 0) {
        switch (max) {
            case r:
                h = ((g - b) / delta) % 6;
                break;
            case g:
                h = (b - r) / delta + 2;
                break;
            case b:
                h = (r - g) / delta + 4;
                break;
            default:
                break;
        }
        h *= 60;
        if (h < 0) h += 360;
    }

    return { h, s: s * 100, l: l * 100 };
};
const parseColorToRgb = (color: string): { r: number; g: number; b: number } | null => {
    if (!color) return null;

    const rgbaMatch = color.replace(/\s+/g, '').match(/^rgba?\((\d+),(\d+),(\d+)(?:,[0-9.]+)?\)$/i);

    if (rgbaMatch) {
        const r = Number(rgbaMatch[1]);
        const g = Number(rgbaMatch[2]);
        const b = Number(rgbaMatch[3]);
        if ([r, g, b].every(v => Number.isFinite(v))) {
            return { r, g, b };
        }
    }

    const hexMatch = color.replace('#', '').match(/^([0-9a-f]{6})$/i);
    if (hexMatch) {
        const int = parseInt(hexMatch[1], 16);
        return {
            // eslint-disable-next-line no-bitwise
            r: (int >> 16) & 255,
            // eslint-disable-next-line no-bitwise
            g: (int >> 8) & 255,
            // eslint-disable-next-line no-bitwise
            b: int & 255,
        };
    }

    return null;
};

const rgbToHsl = (r: number, g: number, b: number): { h: number; s: number; l: number } => {
    const rn = r / 255;
    const gn = g / 255;
    const bn = b / 255;

    const max = Math.max(rn, gn, bn);
    const min = Math.min(rn, gn, bn);
    const delta = max - min;

    let h = 0;
    const l = (max + min) / 2;
    const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));

    if (delta !== 0) {
        switch (max) {
            case rn:
                h = ((gn - bn) / delta) % 6;
                break;
            case gn:
                h = (bn - rn) / delta + 2;
                break;
            case bn:
                h = (rn - gn) / delta + 4;
                break;
            default:
                break;
        }
        h *= 60;
        if (h < 0) h += 360;
    }

    return { h, s: s * 100, l: l * 100 };
};

// HSL (0–360, 0–100, 0–100) -> RGB (0–255)
const hslToRgb = (h: number, s: number, l: number): { r: number; g: number; b: number } => {
    // eslint-disable-next-line no-underscore-dangle
    const _s = s / 100;
    // eslint-disable-next-line no-underscore-dangle
    const _l = l / 100;

    const c = (1 - Math.abs(2 * _l - 1)) * _s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = _l - c / 2;

    let r1 = 0;
    let g1 = 0;
    let b1 = 0;

    if (h >= 0 && h < 60) {
        r1 = c;
        g1 = x;
        b1 = 0;
    } else if (h >= 60 && h < 120) {
        r1 = x;
        g1 = c;
        b1 = 0;
    } else if (h >= 120 && h < 180) {
        r1 = 0;
        g1 = c;
        b1 = x;
    } else if (h >= 180 && h < 240) {
        r1 = 0;
        g1 = x;
        b1 = c;
    } else if (h >= 240 && h < 300) {
        r1 = x;
        g1 = 0;
        b1 = c;
    } else {
        r1 = c;
        g1 = 0;
        b1 = x;
    }

    return {
        r: Math.round((r1 + m) * 255),
        g: Math.round((g1 + m) * 255),
        b: Math.round((b1 + m) * 255),
    };
};

export const getDistinctColorRgba = (baseColors: string[], alpha = 1): string => {
    const existingHues: number[] = [];

    baseColors.forEach(c => {
        const rgb = parseColorToRgb(c);
        if (!rgb) return;
        const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
        if (Number.isFinite(hsl.h)) {
            existingHues.push(hsl.h);
        }
    });

    // Fallback if everything failed to parse
    if (!existingHues.length) {
        return `rgba(0, 128, 255, ${alpha})`;
    }

    let bestHue = 0;
    let bestScore = -1;

    for (let h = 0; h < 360; h += 1) {
        const minDistToExisting = Math.min(
            ...existingHues.map(eh => {
                const d = Math.abs(h - eh);
                return Math.min(d, 360 - d);
            }),
        );

        if (minDistToExisting > bestScore) {
            bestScore = minDistToExisting;
            bestHue = h;
        }
    }

    const sat = 65;
    const light = 55;

    const { r, g, b } = hslToRgb(bestHue, sat, light);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

function formatCompactNumber(value: number): string {
    if (value >= 1_000_000_000) {
        return `${(value / 1_000_000_000).toFixed(2)}B`;
    }
    if (value >= 1_000_000) {
        return `${(value / 1_000_000).toFixed(2)}M`;
    }
    return value.toLocaleString();
}

const WrappedAxisTick: React.FC<WrappedAxisTickProps> = ({
    x = 0,
    y = 0,
    payload,
    splitCamelCase = false,
    fontSize = 15,
}) => {
    const raw = payload?.value ?? '';

    const spaced = splitCamelCase ? raw.replace(/([a-z])([A-Z])/g, '$1 $2') : raw;

    const words = spaced.split(' ');

    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} textAnchor="middle" fill="#666" fontSize={`${fontSize}px`}>
                {words.map((word, index) => (
                    <tspan
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        x={0}
                        dy={index === 0 ? '1.2em' : '1.1em'}
                    >
                        {word}
                    </tspan>
                ))}
            </text>
        </g>
    );
};

const FirstTab = ({
    loading,
    impactsByEnvironmentData,
    pieAllImpactsData,
    campaignImpactsData,
}) => {
    const topMediaOwnerBarCardIndex = 4;
    const topEnvironmentCardIndex = 5;
    const t = useCommonTranslation();
    const { user } = useAuthenticatedUser();
    const hideZeroOverdelivery = useMemo(
        () =>
            user?.email != null &&
            HIDE_ZERO_OVERDELIVERY_EMAILS.includes(user.email.trim().toLowerCase()),
        [user?.email],
    );
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const [chartWidth, setChartWidth] = React.useState<number>(0);
    const currentYear = new Date().getFullYear();

    const [expandedCardIndex, setExpandedCardIndex] = React.useState<number | null>(null);

    useEffect(() => {
        if (!chartContainerRef.current) return;
        if (loading) return;

        const observer = new ResizeObserver(entries => {
            const entry = entries[0];
            setChartWidth(entry.contentRect.width);
        });

        observer.observe(chartContainerRef.current);

        // eslint-disable-next-line consistent-return
        return () => observer.disconnect();
    }, [loading]);

    const totals = useMemo(() => {
        const rows = impactsByEnvironmentData?.data ?? [];
        if (!rows.length) return { expected: 0, received: 0, additional: 0 };

        const { expected, received } = rows.reduce(
            (acc, curr) => {
                const inSchedule = curr.impacts_received_in_schedule_sum ?? 0;
                const outOfSchedule = curr.impacts_received_out_of_schedule_sum ?? 0;
                const unbooked = curr.impacts_received_unbooked_sum ?? 0;

                acc.expected += curr.expected_impacts_in_schedule_sum ?? 0;
                acc.received += inSchedule + outOfSchedule + unbooked;
                return acc;
            },
            { expected: 0, received: 0 },
        );

        const additional = received - expected;
        return { expected, received, additional };
    }, [impactsByEnvironmentData]);

    const impactDeliveryData = useMemo(() => {
        if (!pieAllImpactsData?.data?.length) return [];

        const total = pieAllImpactsData.data.reduce(
            (acc, curr) => {
                acc.expected += curr.expected_impacts_in_schedule_sum;
                acc.inSchedule += curr.impacts_received_in_schedule_sum;
                acc.outOfSchedule += curr.impacts_received_out_of_schedule_sum;
                acc.unbooked += curr.impacts_received_unbooked_sum;
                return acc;
            },
            { expected: 0, inSchedule: 0, outOfSchedule: 0, unbooked: 0 },
        );

        return [
            {
                name: t('Modules.Main.Dashboard.expectedImpacts'),
                inSchedule: total.expected,
                outOfSchedule: 0,
                unbooked: 0,
            },
            {
                name: t('Modules.Main.Dashboard.totalImpacts'),
                inSchedule: total.inSchedule,
                outOfSchedule: total.outOfSchedule,
                unbooked: total.unbooked,
            },
        ];
    }, [pieAllImpactsData, t]);

    const mediaOwnersShareData = useMemo(() => {
        if (!pieAllImpactsData?.data?.length) return [];

        const getTotalImpactsForOwner = (item: any) =>
            item.impacts_received_in_schedule_sum +
            item.impacts_received_out_of_schedule_sum +
            item.impacts_received_unbooked_sum;

        const totalImpacts = pieAllImpactsData.data.reduce(
            (sum, item) => sum + getTotalImpactsForOwner(item),
            0,
        );

        const rawShares = pieAllImpactsData.data.map(item => {
            const ownerTotal = getTotalImpactsForOwner(item);
            return {
                mediaOwner: (item as any).display_owner,
                sharePercent: totalImpacts ? (ownerTotal / totalImpacts) * 100 : 0,
            };
        });

        const sorted = rawShares.sort((a, b) => b.sharePercent - a.sharePercent);

        // ⬇️ now 5 + Others
        const topFive = sorted.slice(0, 5);
        const others = sorted.slice(5);

        if (others.length) {
            const othersTotal = others.reduce((sum, d) => sum + d.sharePercent, 0);
            topFive.push({ mediaOwner: 'Others', sharePercent: othersTotal });
        }

        const balanced = [];
        let left = 0;
        let right = topFive.length - 1;
        while (left <= right) {
            if (left === right) {
                balanced.push(topFive[left]);
            } else {
                balanced.push(topFive[left], topFive[right]);
            }
            // eslint-disable-next-line no-plusplus
            left++;
            // eslint-disable-next-line no-plusplus
            right--;
        }

        return balanced;
    }, [pieAllImpactsData]);

    const totalImpacts = useMemo(() => {
        if (!pieAllImpactsData?.data?.length) return 0;

        const getTotalImpactsForOwner = (item: any) =>
            item.impacts_received_in_schedule_sum +
            item.impacts_received_out_of_schedule_sum +
            item.impacts_received_unbooked_sum;

        return pieAllImpactsData.data.reduce((acc, item) => acc + getTotalImpactsForOwner(item), 0);
    }, [pieAllImpactsData]);

    const mediaOwnersAdditionalImpactsShare = useMemo(() => {
        if (!pieAllImpactsData?.data?.length) return [];

        const getAdditionalImpacts = (item: any) =>
            item.impacts_received_out_of_schedule_sum +
            item.impacts_received_unbooked_sum +
            item.impacts_received_in_schedule_sum -
            item.expected_impacts_in_schedule_sum;

        const totalAdditionalImpacts = pieAllImpactsData.data.reduce(
            (sum, item) => sum + getAdditionalImpacts(item),
            0,
        );

        const rawShares = pieAllImpactsData.data.map(item => {
            const additional = getAdditionalImpacts(item);
            return {
                mediaOwner: (item as any).display_owner,
                sharePercent: totalAdditionalImpacts
                    ? (additional / totalAdditionalImpacts) * 100
                    : 0,
            };
        });

        const positiveShares = rawShares.filter(d => d.sharePercent > 0);
        if (!positiveShares.length) return [];

        const sorted = positiveShares.sort((a, b) => b.sharePercent - a.sharePercent);
        const topFive = sorted.slice(0, 5);
        const others = sorted.slice(5);

        if (others.length) {
            const othersTotal = others.reduce((sum, d) => sum + d.sharePercent, 0);
            topFive.push({ mediaOwner: 'Others', sharePercent: othersTotal });
        }

        const balanced = [];
        let left = 0;
        let right = topFive.length - 1;
        while (left <= right) {
            if (left === right) {
                balanced.push(topFive[left]);
            } else {
                balanced.push(topFive[left], topFive[right]);
            }
            // eslint-disable-next-line no-plusplus
            left++;
            // eslint-disable-next-line no-plusplus
            right--;
        }

        return balanced;
    }, [pieAllImpactsData]);

    const totalAdditionalImpacts = useMemo(() => {
        if (!pieAllImpactsData?.data?.length) return 0;

        return pieAllImpactsData.data.reduce(
            (acc, item) =>
                acc +
                item.impacts_received_out_of_schedule_sum +
                item.impacts_received_unbooked_sum +
                item.impacts_received_in_schedule_sum -
                item.expected_impacts_in_schedule_sum,
            0,
        );
    }, [pieAllImpactsData]);

    const top5MediaOwnersPercentImpacts = useMemo(() => {
        if (!pieAllImpactsData?.data?.length) return [];

        const result = pieAllImpactsData.data
            .map(item => {
                const expected = item.expected_impacts_in_schedule_sum;
                const inSchedule = item.impacts_received_in_schedule_sum;
                const outOfSchedule = item.impacts_received_out_of_schedule_sum;
                const unbooked = item.impacts_received_unbooked_sum;

                const totalDelivered = inSchedule + outOfSchedule + unbooked;

                const percent = expected > 0 ? (totalDelivered / expected) * 100 : 0;

                return {
                    mediaOwner: (item as any).display_owner,
                    performancePercent: Math.round(percent),
                };
            })
            .sort((a, b) => b.performancePercent - a.performancePercent)
            .slice(0, 5);

        return result;
    }, [pieAllImpactsData]);

    const top5EnvironmentOverdelivery = useMemo(() => {
        if (!impactsByEnvironmentData?.data?.length) return [];

        const result = impactsByEnvironmentData.data
            .map(item => {
                const expected = item.expected_impacts_in_schedule_sum;
                const inSchedule = item.impacts_received_in_schedule_sum;
                const outOfSchedule = item.impacts_received_out_of_schedule_sum;
                const unbooked = item.impacts_received_unbooked_sum;

                const totalDelivered = inSchedule + outOfSchedule + unbooked;

                const percent = expected > 0 ? (totalDelivered / expected) * 100 : 0;

                return {
                    environment: (item as any).display_env,
                    overdeliveryPercent: Math.round(percent),
                };
            })
            .filter(item => !hideZeroOverdelivery || item.overdeliveryPercent !== 0)
            .sort((a, b) => b.overdeliveryPercent - a.overdeliveryPercent)
            .slice(0, 5);

        return result;
    }, [impactsByEnvironmentData, hideZeroOverdelivery]);
    const handleCardClick = (row: number, index: number) => {
        const globalIndex = row * 3 + index;
        setExpandedCardIndex(prev => (prev === globalIndex ? null : globalIndex));
    };
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    // chart text colours, kept as before in light mode
    const chartTextMuted = isDarkMode ? theme.palette.text.secondary : '#666';
    const chartTextStrong = isDarkMode ? theme.palette.text.primary : '#000';
    // echarts outlines pie labels in white, which is unreadable on a dark card
    // echarts tooltips default to a white box
    const echartsTooltipColors = useMemo(
        () =>
            isDarkMode
                ? {
                      backgroundColor: theme.palette.background.paper,
                      borderColor: theme.palette.divider,
                      textStyle: { color: theme.palette.text.primary },
                  }
                : {},
        [isDarkMode, theme],
    );
    const pieLabelColors = useMemo(
        () => (isDarkMode ? { color: theme.palette.text.primary, textBorderWidth: 0 } : {}),
        [isDarkMode, theme],
    );

    const baseColors = useMemo(
        () =>
            [
                theme.palette.primary?.main,
                theme.palette.secondary?.main,
                (theme.palette as any).tertiary?.main,
                (theme.palette as any).quaternary?.main,
                (theme.palette as any).quinary?.main,
                (theme.palette as any).senary?.main,
            ].filter((c): c is string => typeof c === 'string' && c.trim().length > 0),
        [theme],
    );

    const COLORS = useMemo(() => {
        const target = 6;
        const out = [...baseColors];

        while (out.length < target) {
            out.push(getDistinctColorRgba(out, 1));
        }

        return out;
    }, [baseColors]);

    const allImpactsOption = useMemo(() => {
        if (!mediaOwnersShareData.length) return {};

        return {
            color: COLORS,
            legend: { show: false },
            tooltip: {
                ...echartsTooltipColors,
                trigger: 'item',
                formatter: (params: any) => {
                    const { name, value, percent } = params;
                    const rawImpactValue = (value / 100) * totalImpacts;

                    return pieTooltipHtml(
                        name,
                        t('Modules.Main.Dashboard.allImpacts'),
                        formatCompactNumber(rawImpactValue),
                        percent,
                    );
                },
            },
            series: [
                {
                    name: t('Modules.Main.Dashboard.allImpacts'),
                    type: 'pie',
                    radius: '70%',
                    center: ['50%', '50%'],
                    avoidLabelOverlap: true,
                    label: {
                        show: true,
                        formatter: '{b}',
                        fontSize: 12,
                        ...pieLabelColors,
                    },
                    labelLine: {
                        show: true,
                        length: 10,
                        length2: 10,
                        smooth: true,
                    },
                    data: mediaOwnersShareData.map(d => ({
                        name: d.mediaOwner,
                        value: d.sharePercent,
                    })),
                },
            ],
        };
    }, [mediaOwnersShareData, totalImpacts, t, COLORS, pieLabelColors, echartsTooltipColors]);

    const additionalImpactsOption = useMemo(() => {
        if (!mediaOwnersAdditionalImpactsShare.length) return {};

        return {
            color: COLORS,
            legend: { show: false },
            tooltip: {
                ...echartsTooltipColors,
                trigger: 'item',
                formatter: (params: any) => {
                    const { name, value, percent } = params; // value = sharePercent
                    const rawImpactValue = (value / 100) * totalAdditionalImpacts;

                    return pieTooltipHtml(
                        name,
                        t('Modules.Main.Dashboard.additionalImpacts'),
                        formatCompactNumber(rawImpactValue),
                        percent,
                    );
                },
            },
            series: [
                {
                    name: t('Modules.Main.Dashboard.additionalImpacts'),
                    type: 'pie',
                    radius: '70%',
                    center: ['50%', '50%'],
                    avoidLabelOverlap: true,
                    label: {
                        show: true,
                        formatter: '{b}',
                        fontSize: 12,
                        ...pieLabelColors,
                    },
                    labelLine: {
                        show: true,
                        length: 10,
                        length2: 10,
                        smooth: true,
                    },
                    data: mediaOwnersAdditionalImpactsShare.map(d => ({
                        name: d.mediaOwner,
                        value: d.sharePercent,
                    })),
                },
            ],
        };
    }, [
        mediaOwnersAdditionalImpactsShare,
        totalAdditionalImpacts,
        t,
        COLORS,
        pieLabelColors,
        echartsTooltipColors,
    ]);

    const impactPerformanceOption = useMemo(() => {
        if (!pieAllImpactsData?.data?.length) return {};

        const total = pieAllImpactsData.data.reduce(
            (acc, curr) => {
                acc.expected += curr.expected_impacts_in_schedule_sum;
                acc.delivered +=
                    curr.impacts_received_in_schedule_sum +
                    curr.impacts_received_out_of_schedule_sum +
                    curr.impacts_received_unbooked_sum;
                return acc;
            },
            { expected: 0, delivered: 0 },
        );

        const isOverDelivered = total.delivered >= total.expected;
        const additional = Math.abs(total.delivered - total.expected);
        const percent = total.expected > 0 ? (total.delivered / total.expected) * 100 : 0;

        const labelY = 225;
        const centerX = chartWidth / 2;
        const offset = 110;

        return {
            tooltip: {
                ...echartsTooltipColors,
                trigger: 'item',
                formatter: ({ name, value }: any) =>
                    `${format.encodeHTML(name)}: ${format.encodeHTML(formatCompactNumber(value))}`,
            },
            graphic: [
                {
                    type: 'text',
                    position: [centerX - 130, labelY],
                    style: {
                        text: '0',
                        fontSize: 14,
                        fill: chartTextMuted,
                        fontWeight: 'normal',
                    },
                },
                {
                    type: 'text',
                    position: [centerX + offset, labelY],
                    style: {
                        text: formatCompactNumber(total.delivered),
                        fontSize: 14,
                        fill: chartTextMuted,
                        fontWeight: 'normal',
                    },
                },
                {
                    type: 'text',
                    left: 'center',
                    top: '60%',
                    style: {
                        text: `${percent.toFixed(1)}%`,
                        fontSize: 28,
                        fontWeight: 'bold',
                        fill: chartTextStrong,
                    },
                },
            ],
            series: [
                {
                    type: 'pie',
                    radius: ['70%', '100%'],
                    center: ['50%', '70%'],
                    startAngle: 180,
                    label: {
                        show: true,
                        position: 'outside',
                        formatter: '{b}',
                        fontSize: 12,
                        ...pieLabelColors,
                    },
                    labelLine: {
                        show: true,
                    },
                    data: [
                        {
                            value: Math.min(total.expected, total.delivered),
                            name: 'Expected',
                            itemStyle: { color: theme.palette.primary.main },
                        },
                        isOverDelivered
                            ? {
                                  value: additional,
                                  name: 'Additional',
                                  itemStyle: { color: theme.palette.secondary.main },
                              }
                            : {
                                  value: additional,
                                  name: 'Under',
                                  itemStyle: {
                                      color: theme.palette.primary.main,
                                      opacity: 0.3,
                                  },
                              },
                        {
                            value: Math.max(total.expected, total.delivered),
                            name: '',
                            itemStyle: { color: 'none' },
                            tooltip: { show: false },
                            label: { show: false },
                            emphasis: { disabled: true },
                        },
                    ],
                },
            ],
        };
    }, [
        chartWidth,
        pieAllImpactsData,
        theme,
        chartTextMuted,
        chartTextStrong,
        pieLabelColors,
        echartsTooltipColors,
    ]);

    const firstRowCards = [
        {
            title: `${t('Modules.Main.Dashboard.additionalImpacts')} ${currentYear}`,
            content: (
                <Box ref={chartContainerRef}>
                    <ReactECharts style={{ height: 240 }} option={impactPerformanceOption} />
                </Box>
            ),
        },
        {
            title: t('Modules.Main.Dashboard.impactDelivery'),
            content: (
                <ResponsiveContainer width="100%" height={240}>
                    <BarChart
                        layout="vertical"
                        data={impactDeliveryData}
                        margin={{ top: 10, right: 30, left: 100, bottom: 10 }}
                        barCategoryGap={20}
                    >
                        <XAxis
                            type="number"
                            domain={[0, 2.6]}
                            tickFormatter={formatCompactNumber}
                        />
                        <YAxis
                            type="category"
                            dataKey="name"
                            tick={{ fontSize: 14, fill: chartTextMuted }}
                        />
                        <Tooltip
                            formatter={(value, name) => [
                                formatCompactNumber(value as number),
                                name,
                            ]}
                        />

                        <Bar
                            dataKey="inSchedule"
                            stackId="a"
                            fill={COLORS[0]}
                            name="In Schedule"
                            radius={[6, 6, 6, 6]}
                        />
                        <Bar
                            dataKey="unbooked"
                            stackId="a"
                            fill={COLORS[1]}
                            name="Unbooked"
                            radius={[6, 6, 6, 6]}
                        />
                        <Bar
                            dataKey="outOfSchedule"
                            stackId="a"
                            fill={COLORS[2]}
                            name="Out of Schedule"
                            radius={[6, 6, 6, 6]}
                        />
                    </BarChart>
                </ResponsiveContainer>
            ),
        },
        {
            title: `${t('Modules.Main.Dashboard.topFiveMediaOwners')} (${t(
                'Modules.Main.Dashboard.allImpacts',
            )})`,
            content: (
                <Box>
                    <ReactECharts style={{ height: 240 }} option={allImpactsOption} />
                </Box>
            ),
        },
    ];
    const secondRowCards = useMemo(
        () => [
            {
                title: `${t('Modules.Main.Dashboard.topFiveMediaOwners')} (${t(
                    'Modules.Main.Dashboard.additionalImpacts',
                )})`,
                content: (
                    <Box>
                        <ReactECharts style={{ height: 240 }} option={additionalImpactsOption} />
                    </Box>
                ),
            },

            {
                title: `${t('Modules.Main.Dashboard.topFiveMediaOwners')} (${t(
                    'Modules.Main.Dashboard.percImpacts',
                )})`,
                content: (
                    <ResponsiveContainer width="100%" height={240}>
                        <BarChart
                            data={top5MediaOwnersPercentImpacts}
                            margin={{ top: 10, right: 30, left: 30, bottom: 30 }}
                        >
                            <XAxis
                                dataKey="mediaOwner"
                                interval={0}
                                tick={
                                    <WrappedAxisTick
                                        fontSize={
                                            expandedCardIndex === topMediaOwnerBarCardIndex
                                                ? 15
                                                : 11
                                        }
                                    />
                                }
                            />
                            <YAxis domain={[0, 200]} tickFormatter={v => `${v}%`} />
                            <Tooltip formatter={v => `${v}%`} />
                            <Bar
                                dataKey="performancePercent"
                                name={t('Modules.Main.Dashboard.percImpactPerformance')}
                                fill={COLORS[0]}
                                radius={[8, 8, 0, 0]}
                            />
                            <ReferenceLine y={100} stroke={COLORS[1]} />
                        </BarChart>
                    </ResponsiveContainer>
                ),
            },
            {
                title: `${t('Modules.Main.Dashboard.TopFivePerformingEnvrionments')} (${t(
                    `Modules.Main.Dashboard.percImpacts`,
                )})`,
                content: (
                    <ResponsiveContainer width="100%" height={240}>
                        <BarChart
                            data={top5EnvironmentOverdelivery}
                            margin={{ top: 10, right: 30, left: 30, bottom: 30 }}
                        >
                            <XAxis
                                dataKey="environment"
                                interval={0}
                                tick={
                                    <WrappedAxisTick
                                        splitCamelCase
                                        fontSize={
                                            expandedCardIndex === topEnvironmentCardIndex ? 15 : 11
                                        }
                                    />
                                }
                            />
                            <YAxis domain={[0, 500]} tickFormatter={v => `${v}%`} />
                            <Tooltip formatter={v => `${v}%`} />
                            <Bar
                                dataKey="overdeliveryPercent"
                                name={t('Modules.Main.Dashboard.percImpactPerformance')}
                                fill={COLORS[0]}
                                radius={[8, 8, 0, 0]}
                            />
                            <ReferenceLine y={100} stroke={COLORS[1]} />
                        </BarChart>
                    </ResponsiveContainer>
                ),
            },
        ],
        [
            t,
            additionalImpactsOption,
            top5MediaOwnersPercentImpacts,
            COLORS,
            top5EnvironmentOverdelivery,
            expandedCardIndex,
            topEnvironmentCardIndex,
            topMediaOwnerBarCardIndex,
        ],
    );
    const reorderedCards = React.useMemo(() => {
        if (expandedCardIndex !== null && expandedCardIndex >= 3) {
            const actualIndex = expandedCardIndex - 3;
            const expanded = secondRowCards[actualIndex];
            const rest = secondRowCards.filter((_, i) => i !== actualIndex);
            return [expanded, ...rest];
        }
        return secondRowCards;
    }, [expandedCardIndex, secondRowCards]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Stack spacing={2}>
            <Card>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Typography variant="h1" align="center" sx={{ mt: 2 }}>
                        {currentYear} Campaigns
                    </Typography>
                </CardContent>
            </Card>

            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card style={{ borderRadius: 10 }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Typography
                                variant="h3"
                                align="center"
                                color={theme.palette.text.disabled}
                            >
                                {t('Modules.Main.Dashboard.campaigns')}
                            </Typography>
                            <Typography variant="h3" align="center">
                                {campaignImpactsData?.data?.length}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card style={{ borderRadius: 10 }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Typography
                                variant="h3"
                                align="center"
                                color={theme.palette.text.disabled}
                            >
                                {t('Modules.Main.Dashboard.targetImpacts')}
                            </Typography>
                            <Typography variant="h3" align="center">
                                {formatCompactNumber(totals.expected)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card style={{ borderRadius: 10 }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Typography
                                variant="h3"
                                align="center"
                                color={theme.palette.text.disabled}
                            >
                                {t('Modules.Main.Dashboard.totalImpacts')}
                            </Typography>
                            <Typography variant="h3" align="center">
                                {formatCompactNumber(totals.received)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card style={{ borderRadius: 10 }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Typography
                                variant="h3"
                                align="center"
                                color={theme.palette.text.disabled}
                            >
                                {t('Modules.Main.Dashboard.additionalImpacts')}
                            </Typography>
                            <Typography variant="h3" align="center">
                                {formatCompactNumber(totals.additional)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                {firstRowCards.map((card, index) => (
                    <Grid key={card.title} item xs={12} md={6} lg={4}>
                        <Card style={{ borderRadius: 12 }}>
                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                <Typography variant="h6">{card.title}</Typography>
                                <Box>{card.content}</Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
            {(() => (
                <Grid container spacing={2}>
                    {reorderedCards.map((card, visualIndex) => {
                        const actualIndex = secondRowCards.indexOf(card);
                        const globalIndex = 3 + actualIndex;
                        const isExpanded = expandedCardIndex === globalIndex;

                        let gridProps: { xs: number; md?: number } = {
                            xs: 12,
                            md: 4,
                        };

                        if (expandedCardIndex !== null && expandedCardIndex >= 3) {
                            if (isExpanded) {
                                gridProps = { xs: 12 };
                            } else {
                                gridProps = { xs: 12, md: 6 };
                            }
                        }

                        const showClose = isExpanded && (actualIndex !== 0 || visualIndex !== 0);

                        const isUnexpandable = actualIndex === 0 && visualIndex === 0;

                        return (
                            <Grid
                                key={card.title}
                                item
                                {...gridProps}
                                style={{ transition: 'all 0.3s ease-in-out' }}
                            >
                                <Card style={{ borderRadius: 12 }}>
                                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                        >
                                            <Typography variant="h6">{card.title}</Typography>
                                            {showClose && (
                                                <IconButton
                                                    onClick={() => setExpandedCardIndex(null)}
                                                >
                                                    <CloseIcon />
                                                </IconButton>
                                            )}
                                        </Stack>
                                        <Box
                                            onClick={
                                                isUnexpandable
                                                    ? undefined
                                                    : () => handleCardClick(1, actualIndex)
                                            }
                                            style={{
                                                cursor: isUnexpandable
                                                    ? 'default'
                                                    : isExpanded
                                                    ? 'default'
                                                    : 'pointer',
                                                marginTop: '8px',
                                            }}
                                        >
                                            {card.content}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            ))()}
        </Stack>
    );
};

export default FirstTab;
